import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlanContext - Global state management for user plan and balance information
 * 
 * Provides:
 * - planInfo: Current plan details (plan, quota, usage, wallet_balance)
 * - user: Current user info
 * - refreshPlanInfo: Function to fetch latest plan info from server
 * - updateFromApiResponse: Function to update balance from API response
 * 
 * TASK 1 IMPLEMENTATION:
 * Every API response that deducts words should include:
 * - remaining_balance / remaining_words
 * - monthly_usage (for subscription plans)
 * This context intercepts these values and updates the navbar immediately.
 */

const PlanContext = createContext(null);

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      user: null,
      planInfo: null,
      loading: false,
      refreshPlanInfo: async () => null,
      updateFromApiResponse: () => {},
      logout: () => {},
    };
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refresh plan info from server - MUST be defined before useEffects that use it
  const refreshPlanInfo = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/pricing/plan-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPlanInfo(data);
        localStorage.setItem('plan_info', JSON.stringify(data));
        
        // Also update user if included
        if (data.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        
        return data;
      }
    } catch (error) {
      console.error('Failed to refresh plan info:', error);
    }
    return null;
  }, []);

  // Load user and plan info on mount
  useEffect(() => {
    const loadInitialData = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedPlanInfo = localStorage.getItem('plan_info');

      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Failed to parse stored user:', e);
        }
      }

      if (storedPlanInfo) {
        try {
          setPlanInfo(JSON.parse(storedPlanInfo));
        } catch (e) {
          console.error('Failed to parse stored plan info:', e);
        }
      }

      // Fetch fresh data if we have a token
      if (token) {
        await refreshPlanInfo();
        // Also fetch user data if not in localStorage (e.g. after impersonation)
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
          try {
            const meRes = await fetch(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (meRes.ok) {
              const meData = await meRes.json();
              setUser(meData);
              localStorage.setItem('user', JSON.stringify(meData));
            }
          } catch (e) {
            console.error('Failed to fetch user data:', e);
          }
        }
      }

      setLoading(false);
    };

    loadInitialData();
  }, [refreshPlanInfo]);

  // TASK 2 FIX: Re-fetch balance on page focus/resume (important for PWA standalone)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('token');
        if (token) {
          refreshPlanInfo();
        }
      }
    };

    const handleFocus = () => {
      const token = localStorage.getItem('token');
      if (token) {
        refreshPlanInfo();
      }
    };

    // Listen for visibility changes (tab switch, PWA resume)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Listen for window focus (PWA standalone, returning to app)
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshPlanInfo]);

  /**
   * Update balance from API response
   * Call this after any API that deducts credits/words
   * 
   * Expected response fields:
   * - remaining_balance: Updated credit/word balance
   * - remaining_words: Alternative name for remaining_balance  
   * - monthly_usage: Updated monthly usage for subscription plans
   * - wallet_balance: Updated wallet balance for PAYG users
   * - deduction_details: Detailed breakdown object
   */
  const updateFromApiResponse = useCallback((response) => {
    if (!response) return;

    setPlanInfo(prev => {
      if (!prev) return prev;

      const updated = { ...prev };
      const plan = prev.plan;

      // Handle subscription plans (starter, professional, business)
      if (plan === 'starter' || plan === 'professional' || plan === 'business') {
        // Update monthly_usage if provided
        if (response.monthly_usage !== undefined) {
          updated.monthly_usage = response.monthly_usage;
        }
        // Also check deduction_details
        if (response.deduction_details?.usage_after !== undefined) {
          updated.monthly_usage = response.deduction_details.usage_after;
        }
      }

      // Handle PAYG plans
      if (plan === 'payg') {
        if (response.wallet_balance !== undefined) {
          updated.wallet_balance = response.wallet_balance;
        }
        if (response.deduction_details?.wallet_after !== undefined) {
          updated.wallet_balance = response.deduction_details.wallet_after;
        }
      }

      // Generic balance update (for credits-based systems)
      if (response.remaining_balance !== undefined) {
        updated.credits_balance = response.remaining_balance;
      }
      if (response.remaining_words !== undefined) {
        updated.remaining_words = response.remaining_words;
      }

      // Only update if something changed
      if (JSON.stringify(updated) !== JSON.stringify(prev)) {
        localStorage.setItem('plan_info', JSON.stringify(updated));
        return updated;
      }

      return prev;
    });
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('plan_info');
    setUser(null);
    setPlanInfo(null);
  }, []);

  // Set user (used after login)
  const setUserAndPlan = useCallback((userData, planData) => {
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    if (planData) {
      setPlanInfo(planData);
      localStorage.setItem('plan_info', JSON.stringify(planData));
    }
  }, []);

  const value = {
    user,
    planInfo,
    loading,
    refreshPlanInfo,
    updateFromApiResponse,
    logout,
    setUserAndPlan,
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanContext;
